import React, { useEffect, useState } from "react";
import { Box, Select, Spinner, useToast } from "@chakra-ui/react";
import BeefreeSDK from "@beefree.io/sdk";
import { cqAPI } from "../../lib/Auth";
import "./TemplateEditor.css";

const TemplateEditor = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // State for saving spinner
  const toast = useToast();

  const fetchTemplates = async () => {
    setIsLoading(true); // Set loading to true when fetching templates
    try {
      const response = await cqAPI.get(`/campaigns/all-email-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast({
        title: "Error",
        description: "Failed to load email templates.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false); // Set loading to false after fetching
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleBeeFreeSave = async (template, jsonFile, htmlFile) => {
    setIsSaving(true); // Show saving spinner while saving
    try {
      const response = await cqAPI.post('/campaigns/update-email-template', {
        templateId: template.id,
        htmlContent: htmlFile,
        jsonContent: JSON.stringify(jsonFile),
      });
      toast({
        title: "Success",
        description: "Template saved successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await fetchTemplates(); // pull the templates from the database to ensure we are looking at the most recent version
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error",
        description: "Failed to save template.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false); // Hide saving spinner after saving
    }
  };

  const loadTemplateEditor = async (template) => {
    setIsLoading(true); // Show loading spinner while loading editor
    try {
      const response = await cqAPI.get("/campaigns/admin-email-template-edit-token");
      const beeConfig = {
        uid: "0",
        container: "bee-plugin-container",
        language: "en-US",
        onLoad: () => {
          setIsLoading(false); // Hide spinner once the editor is loaded
        },
        onSave: async (jsonFile, htmlFile) => {
          await handleBeeFreeSave(template, jsonFile, htmlFile);
        },
      };

      const builder = new BeefreeSDK(response.data);
      await builder.start(beeConfig, template?.content);
    } catch (error) {
      console.error("Error loading BEE editor:", error);
      toast({
        title: "Error",
        description: "Failed to load BEE editor.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false); // Ensure loading is set to false in case of error
    }
  };

  const handleTemplateSelect = async (event) => {
    const templateId = event.target.value;
    setSelectedTemplate(templateId);

    if (templateId) {
      try {
        const template = templates.find((t) => t.id === templateId);
        if (template) {
          await loadTemplateEditor(template);
        }
      } catch (error) {
        console.error("Error loading template:", error);
        toast({
          title: "Error",
          description: "Failed to load template.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {/* Email Template Dropdown */}
      <Select
        placeholder="Select Email Template"
        value={selectedTemplate}
        onChange={handleTemplateSelect}
        mb={4}
        isDisabled={isLoading || isSaving} // Disable dropdown when loading or saving
      >
        {templates.map((template) => (
          <option key={template.id} value={template?.id}>
            {template?.id}
          </option>
        ))}
      </Select>

      {/* Show spinner while saving */}
      {isSaving && (
        <Spinner size="lg" thickness="4px" speed="0.65s" color="blue.500" />
      )}

      {/* BEE Plugin container */}
      <div id="bee-plugin-container" />
    </Box>
  );
};

export default TemplateEditor;
